import { createListenerMiddleware } from '@reduxjs/toolkit';

import selfServicesApi from 'modules/selfServices/service';
import { AppStartListening } from 'App/ListenerMiddleware';

import { locationChange } from 'modules/router/actions';

import { isLocalKiosk } from 'modules/dealers/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';

import { FinalInvoice } from 'modules/selfServices/types/SelfService';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';

import { getStepFromPath } from 'utils/stepUrlUtils';

import onlinePaymentApi from './service';

import { getStatus } from './selectors';

import { isFinished } from './types/PaymentStatus';

const listenerMiddleware = createListenerMiddleware();
const startAppListening = listenerMiddleware.startListening as AppStartListening;

const POLL_INTERVAL = 5000;

// Poll the final invoice status to prevent WebSocket failure.
startAppListening({
  actionCreator: locationChange,
  effect: async (action, {
    cancelActiveListeners, getState, dispatch, delay, take,
  }) => {
    cancelActiveListeners();

    let state = getState();
    const isKiosk = isLocalKiosk(state);
    // Derive current step from URL
    const { pathname } = action.payload.location;
    let currentStep = getStepFromPath(pathname);

    // We don't poll on 'local' kiosks
    if (!isKiosk && currentStep === SelfServiceSteps.FINAL_INVOICE) {
      let status = getStatus(state);
      const selfServiceId = getSelectedSelfServiceId(state);
      if (!isFinished(status)) {
        let payable = true;

        do {
          await delay(POLL_INTERVAL);

          dispatch(onlinePaymentApi.endpoints.getFinalInvoiceStatus.initiate(
            { selfServiceId },
            { forceRefetch: true },
          ));

          // It's better to wait for the API call using `take` instead of `await` to allow cancellation
          const [{ payload }] = await take(
            (action) => onlinePaymentApi.endpoints.getFinalInvoiceStatus.matchFulfilled(action)
                        || onlinePaymentApi.endpoints.getFinalInvoiceStatus.matchRejected(action),
          );

          state = getState();
          // The status in the store is updated by the previous call and/or by the websocket
          status = getStatus(state);
          // The current step might have changed in the meantime (e.g. websocket update)
          // Derive from URL for consistency with router-based navigation
          currentStep = getStepFromPath(state.router.location.pathname);
          // Final invoice becomes non-payable if the user clicks on "Pay later" button
          payable = (payload as FinalInvoice).payable ?? true;
        } while (payable && !isFinished(status) && currentStep === SelfServiceSteps.FINAL_INVOICE);

        // Fetch the self-service to update the final invoice if we're still on the final invoice step
        // This allows to show the next button or to redirect the user
        if (payable && currentStep === SelfServiceSteps.FINAL_INVOICE) {
          dispatch(selfServicesApi.endpoints.getSelfServiceById.initiate(
            { id: selfServiceId },
            { forceRefetch: true },
          ));
        }
      }
    }
  },
});

export default listenerMiddleware;
